import type {
  ReportFormData,
  NumericField as NumericFieldType,
} from '@/screens/ReportCreation/reportCreation.types';
import { useEffect, useState } from 'react';
import { FieldValues, Path, useController, useWatch } from 'react-hook-form';
import { YStack } from 'tamagui';
import { FieldError } from '../FieldError';
import type { ControlledFieldController } from '../formController.types';
import { NumericField } from './NumericField';

interface ControlledNumericFieldProps<FormValues extends FieldValues> {
  field: NumericFieldType;
  formController: ControlledFieldController<FormValues>;
}

export const ControlledNumericField = <
  FormValues extends FieldValues = ReportFormData,
>({
  field,
  formController,
}: ControlledNumericFieldProps<FormValues>): React.ReactElement => {
  const { field: controllerField, fieldState } = useController<FormValues>({
    ...formController,
  });
  const [isAutoCalculated, setIsAutoCalculated] = useState(false);

  const dependencyValues = useWatch({
    control: formController.control,
    name: (field.autoCalculate?.dependsOn ?? []) as Path<FormValues>[],
  }) as (number | null | undefined)[] | undefined;

  useEffect(() => {
    if (!field.autoCalculate || !field.autoCalculate.dependsOn.length) {
      setIsAutoCalculated(false);
      return;
    }

    const watchedValues = dependencyValues ?? [];
    const hasAllValues =
      watchedValues.length === field.autoCalculate.dependsOn.length &&
      watchedValues.every((value) => typeof value === 'number');

    if (!hasAllValues) {
      setIsAutoCalculated(false);
      return;
    }

    const numericValues = watchedValues as number[];
    const calculatedValue =
      field.autoCalculate.mode === 'sum'
        ? numericValues.reduce((acc, current) => acc + current, 0)
        : undefined;

    if (typeof calculatedValue === 'number') {
      setIsAutoCalculated(true);
      if (controllerField.value !== calculatedValue) {
        controllerField.onChange(calculatedValue);
      }
      return;
    }

    setIsAutoCalculated(false);
  }, [
    controllerField,
    dependencyValues,
    field.autoCalculate,
    field.autoCalculate?.dependsOn,
    field.autoCalculate?.mode,
  ]);

  const value =
    typeof controllerField.value === 'number' ? controllerField.value : null;

  return (
    <YStack gap="$1">
      <NumericField
        label={field.label}
        placeholder={field.placeholder}
        value={value}
        onValueChange={controllerField.onChange}
        required={field.required}
        disabled={field.autoCalculate?.lockWhenCalculated && isAutoCalculated}
      />
      <FieldError message={fieldState.error?.message} />
    </YStack>
  );
};
